// getEnrollCourses()/getDetailedCourse() (Moodle LMS, StudentCourseApi.js)
// aren't pinned to an exact response shape yet, so these getters try common
// Moodle course field names (fullname, shortname, summary, enddate) plus the
// original lesson-plan field spellings as fallbacks, and degrade gracefully
// when a field is absent.
import { format, isValid, parseISO } from "date-fns";

const firstTruthy = (...values) => values.find((value) => value !== undefined && value !== null && value !== "");

export const getCourseId = (course) => firstTruthy(course?.id, course?.courseid, course?.lesson_plan_id) ?? null;

export const getCourseTitle = (course) =>
  firstTruthy(course?.fullname, course?.title, course?.shortname, course?.subject_name, course?.subject?.name) ||
  "Untitled Course";

export const getCourseSubject = (course) =>
  firstTruthy(course?.shortname, course?.subject_name, course?.subject?.name) || "";

export const getCourseClassLabel = (course) =>
  firstTruthy(
    course?.category_name,
    course?.program_name,
    course?.program?.name,
    course?.class_name,
    course?.class?.name
  ) || "";

export const getCourseDate = (course) =>
  firstTruthy(course?.startdate, course?.enddate, course?.lesson_date, course?.date, course?.creation_date) || "";

export const formatCourseDate = (value) => {
  if (!value) return "";
  let date;
  if (typeof value === "string") {
    date = parseISO(value);
  } else if (typeof value === "number") {
    // Moodle's startdate/enddate are Unix timestamps in seconds (e.g.
    // 1774981800), not JS's milliseconds — below 1e12 a value can only be a
    // seconds-based epoch (a ms epoch for any date after 2001 is over 1e12).
    date = new Date(value < 1e12 ? value * 1000 : value);
  } else {
    date = new Date(value);
  }
  return isValid(date) ? format(date, "dd MMM yyyy") : String(value);
};

const getObjectivesList = (course) => {
  const objectives = course?.learning_objectives || course?.objectives || [];
  return Array.isArray(objectives)
    ? objectives.map((item) => (typeof item === "string" ? item : item?.objective || item?.text || "")).filter(Boolean)
    : [];
};

/** Short blurb for the course card. */
export const getCourseDescription = (course) =>
  firstTruthy(course?.description, course?.unit_chapter, getObjectivesList(course)[0], course?.assessment, course?.homework) || "";

/** Full body text for the detail page. */
export const getCourseContent = (course) =>
  firstTruthy(course?.content, course?.description, course?.homework, course?.assessment, course?.unit_chapter) || "";

export const getCourseAttachments = (course) => {
  const materials = course?.materials || course?.attachments || [];
  return (Array.isArray(materials) ? materials : [])
    .map((item) => (typeof item === "string" ? item : firstTruthy(item?.name, item?.url, item?.file_name)))
    .filter(Boolean);
};
